// @flow

import React from "react";
import { withRouter } from "react-router-dom";

import InquiryPage from "buba-components/src/components/InquiryPage";
import { optionsMapper } from "buba-components/src/utils/mappers";
import { FlashMessageConsumer } from "buba-components";
import DataFetcher from "buba-components/src/components/DataFetcher";
import { displayErrorForStatusCode } from "buba-components/src/utils/flashMessages";
import { denormalizeJsonApi } from "buba-components/src/utils/denormalizeJsonApi";
import paths from "../../data/paths";
import { customerInquiryTabs, bankInquiryTabs } from "../../data/navLinks";
import Actions from "./Actions";
import Summary from "./Summary";
import { confirmationOnGuaranteeOptions } from "./options";
import { getAndOpenBankOffer } from "../../api/actions";
import BankHintsWrapper from "./BankHintsWrapper";

type CustomerInquiryDashboardProps = {
  match: {
    params: {
      id: string
    }
  }
};

const initialData = {
  id: "",
  status: "",
  createdAt: "",
  timeInformation: null,
  numberOfUsedBanks: 0,
  assignedAgent: null,
  company: { name: "" },
  guaranteeBank: { state: "" },
  inquiry: {
    id: "",
    amount: 0,
    financialDemand: null,
    productKind: []
  },
  inquiryProcess: { id: "", guaranteeDecision: null },
  inquiryCreator: {
    id: "",
    gender: undefined,
    email: "",
    firstName: "",
    lastName: "",
    phoneNumber: "",
    position: null,
    type: ""
  }
};

const dashboardType = "customer";

const CustomerInquiryDashboard = ({
  match: {
    params: { id }
  }
}: CustomerInquiryDashboardProps) => {
  return (
    <FlashMessageConsumer>
      {({ addMessage }) => (
        <DataFetcher
          initialPayload={{
            id
          }}
          mapper={denormalizeJsonApi}
          onError={msg => addMessage(msg, "failure")}
          apiAction={getAndOpenBankOffer}
          redirectOnError
        >
          {({ data: responseData, isLoading, requestFetch }) => {
            const data = responseData || initialData;

            const {
              guaranteeBank,
              company,
              inquiry,
              status,
              inquiryProcess,
              createdAt,
              timeInformation,
              numberOfUsedBanks,
              assignedAgent,
              inquiryCreator: { type: creator },
              source
            } = data;

            const bankProcessId = data.id;

            const federalState = guaranteeBank && guaranteeBank.state;
            const contactPersonFullName = assignedAgent && assignedAgent.fullName;
            const phoneNumber = assignedAgent && assignedAgent.phoneNumber;
            const companyName = company && company.name;
            const amount = inquiry && inquiry.amount;
            const numberOfBanks = numberOfUsedBanks;
            const selectedDecision = optionsMapper(
              inquiryProcess && inquiryProcess.guaranteeDecision,
              confirmationOnGuaranteeOptions
            );
            const products = Array.isArray(inquiry.productKind)
              ? inquiry.productKind.map(({ kind }) => kind)
              : [];
            const isBankAdvisorType = dashboardType === "bank_advisor";

            return (
              <InquiryPage
                companyName={companyName}
                backPath={isBankAdvisorType ? paths.bankInquiriesList : paths.customerInquiriesList}
                tabs={isBankAdvisorType ? bankInquiryTabs : customerInquiryTabs}
                isLoadingData={isLoading}
                issuedOn={new Date(createdAt)}
                site="bank"
                status={status}
              >
                <>
                  <Summary
                    federalState={federalState}
                    contactPerson={contactPersonFullName}
                    phoneNumber={phoneNumber}
                    company={companyName}
                    amount={amount}
                    numberOfBanks={numberOfBanks}
                    shouldHideIncludingYours={source === "internal_pool"}
                    status={status}
                    decision={selectedDecision}
                    products={products}
                    creator={creator}
                  />
                  <Actions
                    id={bankProcessId}
                    status={status}
                    timeInformation={timeInformation}
                    displayErrorForStatusCode={code => displayErrorForStatusCode(addMessage, code)}
                    onError={msg => addMessage(msg, "failure")}
                    requestFetch={requestFetch}
                    type={dashboardType}
                  />
                  {!isBankAdvisorType && !isLoading && <BankHintsWrapper inquiryId={inquiry.id} />}
                </>
              </InquiryPage>
            );
          }}
        </DataFetcher>
      )}
    </FlashMessageConsumer>
  );
};

export default withRouter(CustomerInquiryDashboard);
